import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CompanyDetailsForm } from '@/components/company/CompanyDetailsForm';
import { CompanyLogoField } from '@/components/company/CompanyLogoField';
import { api, ApiError } from '@/lib/api';
import { type OnboardingCompanyForm } from '@/lib/schemas';

/**
 * CompanyProfileTab — вкладка «Профиль» раздела «Компания» (AK-350).
 *
 * Реквизиты титульной компании (та же форма, что в онбординге) — теперь
 * редактируемые в любой момент. GET /companies → первая компания →
 * CompanyDetailsForm (mode="edit") → PATCH /companies/{id}.
 *
 * Права редактирования финально проверяет бэк (require_structure_write);
 * при 403 на PATCH форма покажет inline-ошибку.
 */

interface CompanyProfile {
  id: string;
  name: string;
  short_name?: string | null;
  legal_entity_name?: string | null;
  inn?: string | null;
  ogrn?: string | null;
  kpp?: string | null;
  legal_address?: string | null;
  actual_address?: string | null;
  director_name?: string | null;
  director_phone?: string | null;
  director_email?: string | null;
  default_currency_code?: string | null;
  brand_color?: string | null;
  logo_url?: string | null;
}

function toDefaults(c: CompanyProfile): Partial<OnboardingCompanyForm> {
  return {
    name: c.name ?? '',
    short_name: c.short_name ?? '',
    legal_entity_name: c.legal_entity_name ?? '',
    inn: c.inn ?? '',
    ogrn: c.ogrn ?? '',
    kpp: c.kpp ?? '',
    legal_address: c.legal_address ?? '',
    actual_address: c.actual_address ?? '',
    director_name: c.director_name ?? '',
    director_phone: c.director_phone ?? '',
    director_email: c.director_email ?? '',
    default_currency_code:
      (c.default_currency_code as OnboardingCompanyForm['default_currency_code']) ??
      'RUB',
    brand_color: c.brand_color ?? '#16a34a',
  };
}

export function CompanyProfileTab() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['company', 'profile'],
    queryFn: async () => {
      const list = await api.get<CompanyProfile[]>('/api/v1/companies');
      return list[0] ?? null;
    },
    staleTime: 60_000,
    // 403 «нет прав» повторять бессмысленно — права от повтора не появятся,
    // а три ретрая держат человека на скелетоне лишние секунды.
    retry: (attempt, err) =>
      !(err instanceof ApiError && err.status >= 400 && err.status < 500) &&
      attempt < 2,
  });

  const company = data ?? null;
  const forbidden = error instanceof ApiError && error.status === 403;

  const onSubmit = async (form: OnboardingCompanyForm) => {
    const payload = {
      name: form.name,
      short_name: form.short_name || null,
      brand_color: form.brand_color || null,
      legal_entity_name: form.legal_entity_name || null,
      inn: form.inn || null,
      ogrn: form.ogrn || null,
      kpp: form.kpp || null,
      legal_address: form.legal_address || null,
      actual_address: form.actual_address || null,
      director_name: form.director_name || null,
      director_phone: form.director_phone || null,
      director_email: form.director_email || null,
      default_currency_code: form.default_currency_code || 'RUB',
    };
    // Свежий тенант приходит вообще без записи компании — тогда та же форма
    // её и заводит. Раньше здесь стоял `if (!company) return;`, и вкладка
    // была тупиком: «завершите онбординг» без единого способа его завершить.
    if (company) {
      await api.patch(`/api/v1/companies/${company.id}`, payload);
    } else {
      await api.post('/api/v1/companies', payload);
    }
    await qc.invalidateQueries({ queryKey: ['company', 'profile'] });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Реквизиты компании</CardTitle>
        <p className="text-sm text-muted-foreground">
          Название, юр. реквизиты и параметры бренда. Используются в отчётах,
          договорах и экспортах.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {error && !isLoading && (
          <Alert variant={forbidden ? 'default' : 'destructive'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center gap-3">
              <span className="flex-1">
                {forbidden
                  ? 'Реквизиты компании доступны администратору компании — попросите его открыть эту вкладку.'
                  : 'Реквизиты не загрузились. Похоже, связь с сервером прервалась.'}
              </span>
              {!forbidden && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  onClick={() => void refetch()}
                >
                  Повторить
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Пустой тенант: записи компании ещё нет — показываем ту же форму
            на создание, а не тупиковую плашку. Первый клиент на свежей схеме
            встречал именно её. */}
        {!isLoading && !error && !company && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Реквизиты ещё не заполнены. Заполните их сейчас — они попадут в
                отчёты, договоры и выгрузки.
              </AlertDescription>
            </Alert>
            <CompanyDetailsForm mode="create" onSubmit={onSubmit} />
          </div>
        )}

        {!isLoading && !error && company && (
          <div className="space-y-6">
            {/* Логотип живёт отдельно от формы реквизитов: он сохраняется
                сразу по выбору файла, без общей кнопки «Сохранить». */}
            <CompanyLogoField
              companyId={company.id}
              logoUrl={company.logo_url ?? null}
              onChange={() => {
                // Ссылка несёт версию (?v=…), поэтому после замены надо
                // перечитать компанию — иначе в шапке останется старая.
                void qc.invalidateQueries({ queryKey: ['company', 'profile'] });
              }}
            />

            <CompanyDetailsForm
              mode="edit"
              defaultValues={toDefaults(company)}
              onSubmit={onSubmit}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CompanyProfileTab;
